import * as React from "react";
import { Alert, Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import RaterManagementPanel from "../../../registrations/registrationDetail/raterManagement/raterManagementPanel";
import { SessionDetailType } from "../../../../services/types/sessionDetailTypes";
import { UserClaimsService } from "../../../../services/currentUserService/currentUserService";
import { DetailsTabPanel } from "../../../../components/common/detailTabPanel/detailTabPanel";
import { Participant } from "../../../../services/types/enterpriseParticipantApiTypes";
import AssessmentListPanel from "./assessmentList/assessmentListPanel";
import PlatformDatesAccordion from "../../../../components/shared/platformsDatesAccordion";
import { useAreaPreference } from "../../../../hooks/useAreaPreference";
import ScoringStatusPanel from "../../../sessions/sessionDetail/assessments/scoringStatus/scoringStatusPanel";

interface RegistrationAssessmentsPanelProps {
    sessionDetails: SessionDetailType | undefined;
    registration: Participant | undefined;
    handleJournalEntry: (keys: string[]) => void;
    readOnly?: boolean;
    clientAdmin?: boolean;
}

const RegistrationAssessmentsPanel: React.FC<RegistrationAssessmentsPanelProps> = (props) => {
    const [searchParams] = useSearchParams();
    const subTabParam = searchParams.get("subTab");
    const forcedSubTab =
        subTabParam !== null && !isNaN(Number(subTabParam)) ? Number(subTabParam) : null;

    const [prefTab, setPrefTab] = useAreaPreference<number>(
        "registrationDetail",
        "assessments.subTab",
        0,
    );
    const [activeTab, setActiveTab] = React.useState<number>(forcedSubTab ?? prefTab);
    const [hideAboutMe, setHideAboutMe] = useAreaPreference<boolean | null>(
        "registrationDetail",
        "assessments.hideAboutMe",
        null,
    );
    const [keyDatesExpanded, setKeyDatesExpanded] = useAreaPreference<boolean>(
        "registrationDetail",
        "assessments.keyDatesExpanded",
        true,
    );

    const [hidePlatformDates, setHidePlatformDates] = React.useState<boolean>(false);

    const updateHidePlatformDates = (value: boolean) => {
        setHidePlatformDates(value);
    };

    const claimsService = new UserClaimsService();

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number | null) => {
        if (newValue != null) {
            setActiveTab(newValue);
            setPrefTab(newValue);
        }
    };

    const isProductSale =
        props.sessionDetails?.typeKey === 7 || props.sessionDetails?.typeKey === 9;

    let tablist: [string, number, React.ReactNode][] = [
        [
            "Assessment List",
            0,
            <AssessmentListPanel
                registration={props.registration}
                readOnly={props.sessionDetails?.typeKey === 7 || props.clientAdmin}
            />,
        ],
        [
            "Assessment Status",
            1,
            <ScoringStatusPanel
                sessionKey={props.sessionDetails?.sessionKey}
                sessionCode={props.sessionDetails?.sessionCode}
                handleJournalEntry={props.handleJournalEntry}
                source="registration"
                participant={props.registration}
                readOnly={props.readOnly}
                hostPlatformKey={props.sessionDetails?.hostingPlatformKey ?? 0}
                surveyPlatformKey={props.sessionDetails?.assessmentPlatformKey ?? 0}
                startDate={props.sessionDetails?.start ?? null}
                isProductSale={isProductSale}
                updateHidePlatformDates={updateHidePlatformDates}
                hideAboutMe={hideAboutMe}
                onAboutMeVisibilityChange={setHideAboutMe}
            />,
        ],
        [
            "Rater Information",
            2,
            <RaterManagementPanel
                sessionKey={props.sessionDetails?.sessionKey ?? 0}
                programCode={props.sessionDetails?.sessionCode ?? ""}
                registration={props.registration}
                readOnly={!claimsService.IsUserInAppRole(3) || props.readOnly}
                hostPlatformKey={props.sessionDetails?.hostingPlatformKey ?? 0}
                surveyPlatformKey={props.sessionDetails?.assessmentPlatformKey ?? 0}
            />,
        ],
    ];

    if (props.clientAdmin) {
        tablist = tablist.filter((tab) => tab[0] !== "Rater Information");
    }

    if (props.sessionDetails === undefined) {
        return (
            <Box>
                <Alert severity="error">Error: Session data not found!</Alert>
            </Box>
        );
    }

    return (
        <>
            <Box
                sx={{
                    width: 1,
                    height: 1,
                    m: 2,
                    marginTop: 2,
                    borderWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Box
                    display={"flex"}
                    justifyContent={"center"}
                    sx={{ borderWidth: 0, flexGrow: 1 }}
                >
                    {tablist.length > 1 ? (
                        <ToggleButtonGroup
                            color="primary"
                            value={activeTab}
                            exclusive
                            onChange={handleTabChange}
                            sx={{ mb: 3 }}
                        >
                            {tablist.map((tab, i) => (
                                <ToggleButton
                                    key={i}
                                    id={`session-details-assessments-tab-${i}`}
                                    value={i}
                                >
                                    {tab[0]}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    ) : null}
                </Box>
                {!hidePlatformDates && (
                    <Box sx={{ padding: 2 }}>
                        <PlatformDatesAccordion
                            readonly={false}
                            sessionDetails={props.sessionDetails}
                            fromMyAccess={false}
                            keyDatesExpanded={keyDatesExpanded}
                            onKeyDatesExpandedChange={setKeyDatesExpanded}
                        />
                    </Box>
                )}
                {tablist.map((tab, i) => (
                    <DetailsTabPanel
                        detailType={"session-details-assessments"}
                        key={tab[0]}
                        value={activeTab}
                        index={i}
                    >
                        {tab[2]}
                    </DetailsTabPanel>
                ))}
            </Box>
        </>
    );
};

export default RegistrationAssessmentsPanel;
